import { StorefrontCart, StorefrontCartItem, StorefrontOrder } from "src/components/checkout/types"
import { ResolvedListProductData } from "src/data/product-list-resolved/types"
import {
  getProductCoupon,
  getProductDiscount,
  getProductImage,
  getProductPrice,
  getProductRegularPrice,
} from "src/data/product-list/mapping"
import { ListProductData } from "src/data/product-list/types"
import { WCAdminOrderCoupon } from "src/data/woocommerce/wcAdminTypes"
import { WCStoreCoupon } from "src/data/woocommerce/wcStoreTypes"
import { META_DATA } from "src/metadata/constants"
import { routes } from "src/routes"
import { TrackingCart, TrackingLineItem, TrackingOrder, TrackingProduct } from "src/tracking/types"
import { normalizeCouponCode } from "src/utilities/coupons"
import { roundPrice } from "src/utilities/price"
import { ProductImageSize, toProductImageCdnUrl } from "src/utilities/productImages"

const emptyEmage = "data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="

function mapCoupons(coupons: (WCAdminOrderCoupon | WCStoreCoupon)[] | undefined) {
  return (coupons || []).map((coupon) => normalizeCouponCode(coupon.code)).sort()
}

export function deductGermanTax<T extends number | undefined>(price: T): T {
  if (price) {
    // prices are maintained with German tax (19%) included
    return (price / 1.19) as T
  }
  return undefined as T
}

export function mapTrackingPagePath(path: string) {
  return META_DATA.origin + path
}

export function mapListProductToTrackingProduct(product: ListProductData): TrackingProduct {
  const image = getProductImage(product)
  const grossPrice = getProductPrice(product)
  const grossRegularPrice = getProductRegularPrice(product)
  const grossDiscount = getProductDiscount(product)
  const coupon = getProductCoupon(product)

  const promotionType =
    coupon?.code && grossRegularPrice
      ? "sale-and-coupon"
      : grossRegularPrice
        ? "sale"
        : coupon?.code
          ? "coupon"
          : undefined

  return {
    id: product.id.toString(),
    sku: product.sku,
    href: mapTrackingPagePath(routes.formatProduct(product.slug)),
    name: product.name,
    hasVariations: "variations" in product && !!product.variations?.length,
    variantName: undefined,
    image: image ? toProductImageCdnUrl(image, ProductImageSize.Tiny) : emptyEmage,
    categories: product.categories?.map((category) => category.name) || [],
    grossOfferPrice: roundPrice(grossPrice),
    grossFullPrice: roundPrice(grossRegularPrice || grossPrice),
    netOfferPrice: roundPrice(deductGermanTax(grossPrice)),
    netFullPrice: roundPrice(deductGermanTax(grossRegularPrice || grossPrice)),
    currencyCode: "EUR",
    promotionType: promotionType,
    promotionId: coupon?.code,
    discount: roundPrice(grossDiscount || 0),
    couponCodes: coupon ? [normalizeCouponCode(coupon.code)] : [],
  }
}

export function mapResolvedListProductToTrackingProduct(product: ResolvedListProductData): TrackingProduct {
  return {
    ...mapListProductToTrackingProduct(product as unknown as ListProductData),
    variantName: product.variationName,
  }
}

function mapWcCartPrice(cartPrice: string | number | null) {
  // cart prices have the format "100" = 1€
  return cartPrice ? Math.round(+cartPrice) / 100 : 0
}

export function mapCartChangeToTrackingLineItem(
  cart: Pick<StorefrontCart, "items">,
  item: Pick<StorefrontCartItem, "prices" | "quantity" | "product">,
  quantityChange?: number,
): TrackingLineItem {
  const product = mapResolvedListProductToTrackingProduct(item.product)
  const quantity = quantityChange || item.quantity
  const index = cart.items.findIndex((cartItem) => cartItem.product.id === item.product.id)

  return {
    ...product,
    currencyCode: item.prices.currency_code,
    quantity: quantity,
    index: index,
  }
}

/** This is necessary because the API cart object does not have an ID. */
function getCartId() {
  return process.env.NODE_ENV === "test"
    ? "cart-id"
    : document.cookie
        .split(";")
        .find((cookie) => cookie.includes("woocommerce_cart_hash"))
        ?.split("=")[1] || ""
}

export function mapCartToTrackingCart(cart: StorefrontCart, paymentMethod?: string): TrackingCart {
  const cartId = getCartId()
  const grossValue = +(cart.totals.total_price || 0)
  const grossShipping = +(cart.totals.total_shipping || 0) + +(cart.totals.total_shipping_tax || 0)
  const netShipping = +(cart.totals.total_shipping || 0)
  const totalTax = +(cart.totals.total_tax || 0)
  const netValue = grossValue - netShipping - totalTax

  return {
    id: cartId,
    grossValue: mapWcCartPrice(grossValue),
    grossShipping: mapWcCartPrice(grossShipping),
    netValue: mapWcCartPrice(netValue),
    netShipping: mapWcCartPrice(netShipping),
    totalTax: mapWcCartPrice(totalTax),
    currencyCode: cart.totals.currency_code,
    discountCodes: mapCoupons(cart.coupons),
    items: cart.items.map((item) => mapCartChangeToTrackingLineItem(cart, item)),
    shippingMethod: cart.shipping_rates[0]?.shipping_rates[0]?.method_id,
    paymentMethod: paymentMethod,
  }
}

function mapOrderLineItemToTrackingLineItem(item: StorefrontOrder["line_items"][0], index: number): TrackingLineItem {
  const product = mapResolvedListProductToTrackingProduct(item.product)

  return {
    ...product,
    currencyCode: "EUR",
    quantity: item.quantity,
    index,
  }
}

export function mapOrderToTrackingOrder(order: StorefrontOrder): TrackingOrder {
  const grossValue = +(order.total || 0)
  const grossShipping = +(order.shipping_total || 0) + +(order.shipping_tax || 0)
  const netShipping = +(order.shipping_total || 0)
  const totalTax = +(order.total_tax || 0)
  const netValue = grossValue - netShipping - totalTax
  const discount = +(order.discount_total || 0) + +(order.discount_tax || 0)

  return {
    transactionId: order.number,
    email: order.billing.email || "",
    grossValue: roundPrice(grossValue),
    grossShipping: roundPrice(grossShipping),
    netValue: roundPrice(netValue),
    netShipping: roundPrice(netShipping),
    totalTax: roundPrice(totalTax),
    currencyCode: order.currency,
    discount: roundPrice(discount),
    couponCodes: mapCoupons(order.coupon_lines),
    items: order.line_items.map((item, index) => mapOrderLineItemToTrackingLineItem(item, index)),
  }
}
